/**
 * Serviço de API - Autenticação
 *
 * CONTEXTO:
 * Encapsula chamadas HTTP relacionadas à autenticação (cadastro, login, validação de token).
 * Reutiliza a instância `clienteApi` definida em `servicos/clienteApi.ts` para garantir
 * configuração centralizada (baseURL, headers, timeout).
 *
 * RESPONSABILIDADES:
 * - registrarUsuario(dados: DadosCadastro): Promise<RespostaCadastro>
 * - fazerLogin(dados: DadosLogin): Promise<RespostaLogin>
 * - validarToken(token: string): Promise<boolean>
 *
 * PADRÕES:
 * - Tipos importados de `../tipos/tiposAutenticacao`
 * - Erros do axios são transformados em mensagens amigáveis em português
 * - Documentação exhaustiva (AI_MANUAL_DE_MANUTENCAO.md)
 */

import type { AxiosError } from 'axios';
import { clienteApi } from './clienteApi';
import type {
  DadosCadastro,
  RespostaCadastro,
  DadosLogin,
  RespostaLogin,
  RespostaRefreshToken,
} from '../tipos/tiposAutenticacao';

/**
 * Helper interno para mapear erros HTTP do axios para mensagens em português.
 * Mantemos mensagens explícitas para facilitar debugging por futuras IAs.
 */
function mapearErroAxiosParaMensagem(error: AxiosError): string {
  // Erro com resposta do servidor
  if (error.isAxiosError && error.response) {
    const status = error.response.status;
    // body pode conter mensagem do backend
    const backendMensagem = (error.response.data as { mensagem?: string; message?: string; })?.mensagem || (error.response.data as { mensagem?: string; message?: string; })?.message;

    if (status === 400) {
      return backendMensagem || 'Dados inválidos: verifique os campos enviados.';
    }
    if (status === 401) {
      return backendMensagem || 'Credenciais inválidas. Verifique seu email e senha.';
    }
    if (status === 409) {
      return backendMensagem || 'Email já cadastrado.';
    }
    // erros genéricos do servidor
    if (status >= 500) {
      return backendMensagem || 'Erro no servidor. Tente novamente mais tarde.';
    }
    // fallback: retornar mensagem do backend se existir
    return backendMensagem || `Erro HTTP ${status}`;
  }

  // Não é um erro axios com resposta (problema de rede, timeout, etc.)
  if (error && error.message) {
    return error.message;
  }

  return 'Erro desconhecido de rede ou formato de resposta inesperado.';
}

/**
 * Registra um novo usuário no sistema.
 *
 * Fluxo:
 * 1. Chama POST /api/core/usuarios/registrar usando a instância `clienteApi`
 * 2. Retorna o objeto RespostaCadastro (resposta.data)
 * 3. Em caso de erro, lança Error com mensagem amigável em português
 *
 * @param dados - Dados de cadastro (email, senha, nome, ...)
 * @returns Promise<RespostaCadastro>
 *
 * @example
 * ```ts
 * import { registrarUsuario } from '@/servicos/servicoApiAutenticacao';
 * try {
 *   const resposta = await registrarUsuario({ email: 'a@b.com', senha: '123456', nome: 'Ana' });
 *   console.log(resposta.usuario.id);
 * } catch (err) {
 *   // err.message já está em português
 * }
 * ```
 */
export async function registrarUsuario(
  dados: DadosCadastro
): Promise<RespostaCadastro> {
  try {
    const resposta = await clienteApi.post<RespostaCadastro>('/api/core/usuarios/registrar', dados);
    return resposta.data;
  } catch (error) {
    const mensagem = mapearErroAxiosParaMensagem(error as AxiosError);
    throw new Error(mensagem);
  }
}

/**
 * Realiza o login do usuário e retorna o contrato RespostaLogin.
 *
 * Fluxo:
 * 1. Chama POST /api/core/usuarios/login
 * 2. Retorna RespostaLogin (access_token, usuario, expires_in)
 * 3. Em caso de 401, lança Error('Credenciais inválidas')
 *
 * @param dados - Credenciais do usuário
 * @returns Promise<RespostaLogin>
 */
export async function fazerLogin(
  dados: DadosLogin
): Promise<RespostaLogin> {
  try {
    const resposta = await clienteApi.post<RespostaLogin>('/api/core/usuarios/login', dados);
    return resposta.data;
  } catch (error) {
    const mensagem = mapearErroAxiosParaMensagem(error as AxiosError);
    throw new Error(mensagem);
  }
}

/**
 * Valida um token no backend (opcional, útil para refresh/health checks).
 *
 * Implementa chamada simplificada para o endpoint `/api/core/usuarios/validar-token`.
 * O backend deve implementar esse endpoint para retornar 200 quando válido.
 * Se o endpoint não existir, a função retorna false em caso de erro.
 *
 * @param token - Token JWT a ser validado
 * @returns boolean indicando se o token é válido
 */
export async function validarToken(token: string): Promise<boolean> {
  try {
    const resposta = await clienteApi.post('/api/core/usuarios/validar-token', { token });
    // Se retornar 200 consideramos válido
    return resposta.status === 200;
  } catch {
    // Não lançar erro: prefira false para fluxos de verificação silenciosa
    return false;
  }
}

/**
 * Renova o access_token usando um refresh_token válido.
 *
 * Fluxo:
 * 1. Chama POST /api/core/usuarios/refresh com o refresh_token
 * 2. Retorna novo access_token se bem-sucedido
 * 3. Em caso de erro (refresh expirado/inválido), lança erro
 *
 * @param refreshToken - Refresh token válido
 * @returns Promise<RespostaRefreshToken> - Novo access_token e expires_in
 */
export async function renovarToken(refreshToken: string): Promise<RespostaRefreshToken> {
  try {
    const resposta = await clienteApi.post<RespostaRefreshToken>(
      '/api/core/usuarios/refresh',
      { refresh_token: refreshToken }
    );
    return resposta.data;
  } catch (error) {
    const mensagem = mapearErroAxiosParaMensagem(error as AxiosError);
    throw new Error(mensagem);
  }
}
